"use client";

import { Alert } from "@/ui/components/Alert";
import { Button } from "@/ui/components/Button";
import { Skeleton } from "@/ui/components/Skeleton";
import { useSignals } from "@/lib/signals/useSignals";
import { SignalStack } from "./SignalsView";
import { SignalEvidence } from "./SignalEvidence";
import { CandidateDossier } from "./CandidateDossier";
import { CandidateKnowledge } from "./CandidateKnowledge";
import styles from "./SignalsView.module.css";

/**
 * A candidate's DOTS tab: everything DOTS knows about them, the signals worth
 * acting on, and their public dossier. `candidateId` is the numeric record id.
 */
export function CandidateDots({ candidateId }: { candidateId: string }) {
  const { feed, feedback, refresh } = useSignals(candidateId);
  const signals = feed.data ?? [];
  return <div className={styles.stack}>
    <CandidateKnowledge candidateId={candidateId} />
    <section className={styles.stack} aria-labelledby="candidate-signals-heading">
      <h3 id="candidate-signals-heading" className={styles.sectionHeading}>Signals</h3>
      {feed.isPending ? <Skeleton variant="rounded" height={140} />
        : feed.isError ? <Alert variant="error" title="Signals couldn’t be loaded" action={{ label: "Try again", onClick: () => void refresh() }}>Please try again in a moment.</Alert>
        : signals.length === 0 ? <p className={styles.muted}>Nothing to act on yet. Signals appear when research on this CV finds something worth raising.</p>
        : <SignalStack signals={signals} onFeedback={(id, value) => feedback.mutate({ id, value })}
          feedbackPending={feedback.isPending ? feedback.variables.id : undefined}
          evidence={(id) => <SignalEvidence id={id} />} showSubject={false} />}
      {feedback.isError && <p className={styles.muted} role="alert">Your feedback wasn’t saved. <Button variant="tertiary" size="sm" onClick={() => feedback.reset()}>Dismiss</Button></p>}
    </section>
    <CandidateDossier candidateId={candidateId} />
  </div>;
}
